// discount-popup.js
// Vanilla JS controller for the discount popup modal + home-page banner.

// discount-config.js sets window.DISCOUNT_BACKEND_URL — on a live store that is
// the Shopify App Proxy path ('/apps/discounts'), i.e. a path on the store's own
// domain, so nothing here ever hardcodes a backend hostname.
var BACKEND_URL = window.DISCOUNT_BACKEND_URL || (
  ['localhost', '127.0.0.1', ''].indexOf(window.location.hostname) !== -1
    ? 'http://localhost:3000'
    : '/apps/discounts'
);

// ---- One-time fallback off the proxy path --------------------------------
// The proxy path only resolves once the app proxy is configured in the Shopify
// app; until then the STORE answers it with its own 404 page, which would leave
// the banner blank for the whole pageview. So the first failing backend request
// switches to the absolute URL discount-config.js kept as a fallback, for the
// rest of the pageview. Every call site below reads BACKEND_URL at call time
// (nothing captures it), so the switch reaches all of them — including the
// /signup redirect and the banner image URLs.
var BACKEND_FALLBACK = window.DISCOUNT_BACKEND_FALLBACK || null;
var backendFellBack = false;

function useFallbackBackend(why) {
  if (backendFellBack || !BACKEND_FALLBACK || BACKEND_FALLBACK === BACKEND_URL) return false;
  backendFellBack = true;
  console.warn('⚠️ "' + BACKEND_URL + '" did not answer (' + why + ') — falling back to ' +
    BACKEND_FALLBACK + '. Set up the Shopify app proxy so customer URLs stay on your own domain.');
  BACKEND_URL = BACKEND_FALLBACK;
  return true;
}

// fetch() against the backend, retried once on the fallback base.
// ONLY a 404 switches. That is the one response meaning "this path does not
// exist here" - the store answering its own not-found page because the app proxy
// is not registered. Any other status (429 from the rate limiter, 5xx during a
// database blip) comes from OUR backend through a working proxy, and abandoning
// the on-domain path for the rest of the pageview over a transient error would
// undo the point of routing through it.
function backendFetch(path, opts) {
  return fetch(BACKEND_URL + path, opts).then(function (res) {
    if (res.status === 404 && useFallbackBackend('HTTP 404')) return fetch(BACKEND_URL + path, opts);
    return res;
  }, function (err) {
    if (useFallbackBackend((err && err.message) || 'network error')) return fetch(BACKEND_URL + path, opts);
    throw err;
  });
}

var STORAGE_KEY = 'discount_active_code';

// Must stay in sync with CAMP_TEMPLATES in dashboard/js/dashboard.js
var CAMP_TEMPLATES = {
  sage: { bg: '#BDC8B3', text: '#16211B', cta: '#2F6B33', ctaText: '#fff' },
  forest: { bg: '#1A3B1E', text: '#E8F0E6', cta: '#8FCF92', ctaText: '#16211B' },
  linen: { bg: '#E8E1D8', text: '#3A2E22', cta: '#2F6B33', ctaText: '#fff' },
};

(function () {
  document.addEventListener('DOMContentLoaded', function () {
    const overlay = document.getElementById('discount-popup-overlay');
    const closeButton = document.getElementById('discount-popup-close');
    const form = document.getElementById('discount-popup-form');
    const submitButton = document.getElementById('discount-popup-submit');
    const messageEl = document.getElementById('discount-popup-message');
    const confettiHost = document.getElementById('discount-popup-confetti');
    const titleEl = document.getElementById('discount-popup-title');
    const subtitleEl = document.getElementById('discount-popup-subtitle');
    const firstNameFieldEl = document.getElementById('discount-first-name-field');
    const lastNameFieldEl = document.getElementById('discount-last-name-field');
    const countryFieldEl = document.getElementById('discount-country-field');
    const phoneFieldEl = document.getElementById('discount-phone-field');
    const bannerEl = document.querySelector('[data-discount-banner]');

    console.log('🎯 Discount popup initialized');
    console.log('📡 Backend URL:', BACKEND_URL);

    // The signup MODAL and the home BANNER are independent features. This used
    // to `return` when the modal markup was missing, which also skipped the
    // banner code further down — so on any page/template that renders the
    // banner section without the modal snippet, the banner silently never
    // appeared. Now only the modal wiring is skipped.
    const modalReady = !!(overlay && closeButton && form && submitButton && messageEl);
    if (!modalReady) {
      console.warn('⚠️ Discount popup elements not found — skipping modal wiring (the banner still renders)');
    }
    if (!modalReady && !bannerEl) return; // genuinely nothing on this page to do

    const submitButtonDefaultHTML = submitButton ? submitButton.innerHTML : '';
    const CONFETTI_EMOJI = ['🎉', '✨', '🎊', '💥'];
    const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const FOCUSABLE_SELECTOR =
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';
    let previouslyFocusedElement = null;
    let activeTrigger = null;
    let activeCampaign = null;

    // ---------- Preview mode ----------
    // Query param appended by the dashboard's Live Preview iframe (see
    // campSyncPreviewFrame in dashboard.js) — never present for real
    // customers. Declared at top level (not nested under the banner-only
    // code below) because product-page.html has no banner element at all,
    // only the popup modal, and that also needs to preview unsaved drafts.
    const PREVIEW_MODE = /(?:^|[?&])preview=1(?:&|$)/.test(window.location.search);
    const previewCampaignByPlacement = {};

    // The storefront's currently-selected language, so the landing page can open
    // in the same language the shopper chose on the store. Reads <html lang> (set
    // by Shopify to the active locale, e.g. "pt-PT", "fr") and falls back to
    // Shopify.locale. Returns a "&lang=xx" query fragment (or "" if unknown).
    function storeLangParam() {
      var l = (document.documentElement.getAttribute('lang') || (window.Shopify && window.Shopify.locale) || '').toLowerCase();
      l = l ? l.slice(0, 2) : '';
      return l ? '&lang=' + encodeURIComponent(l) : '';
    }

    // A data: URI (unsaved upload still in the builder) is used as-is; a
    // saved campaign's bannerUrl/imageUrl is a relative /api path that needs BACKEND_URL.
    function resolveBannerUrl(url) {
      if (!url) return null;
      return /^(data:|https?:)/.test(url) ? url : BACKEND_URL + url;
    }

    // Only meaningful combined with PREVIEW_MODE — only ever set by the
    // dashboard's builder iframes (Step 1 banner / Step 2 landing preview).
    // The "Customer preview" modal (final test-before-publish) deliberately
    // stays preview=1 only, with no edit param, so it remains a faithful,
    // non-editable simulation; the real customer path never sets either flag.
    const EDIT_MODE = PREVIEW_MODE && /(?:^|[?&])edit=1(?:&|$)/.test(window.location.search);

    if (PREVIEW_MODE) {
      window.addEventListener('message', function (event) {
        if (!event.data || event.data.type !== 'campaign-preview-render') return;
        previewCampaignByPlacement[event.data.placement] = event.data.campaign;
      });
      if (window.parent && window.parent !== window) {
        window.parent.postMessage({ type: 'campaign-preview-ready', placement: 'home' }, '*');
        window.parent.postMessage({ type: 'campaign-preview-ready', placement: 'product' }, '*');
      }
    }

    // ---------- Campaign fetch ----------
    const campaignCache = {};

    // Returned when the campaign could not be looked up at all (network error,
    // rate limit, 5xx). Crucially distinct from `null`, which means the server
    // successfully told us nothing is published. Hiding the banner is only
    // correct for `null` — treating a failed request as "no campaign" is what
    // made the banner vanish intermittently, since a single flaky request
    // through the tunnel blanked it for the whole pageview with no retry.
    const FETCH_FAILED = { fetchFailed: true };

    // force=true bypasses the short client-side cache — used by the live-update
    // path so a change the admin just made paints immediately rather than up to
    // 20s later.
    async function fetchActiveCampaign(placement, force) {
      if (!placement) return null;
      const cached = campaignCache[placement];
      if (!force && cached && cached.expiresAt > Date.now()) return cached.data;
      try {
        const path = '/api/campaigns/active?placement=' + encodeURIComponent(placement)
          + (force ? '&t=' + Date.now() : ''); // also defeat any HTTP-layer cache
        console.log('📡 Fetching campaign from:', BACKEND_URL + path);
        const res = await backendFetch(path);
        if (!res.ok) {
          console.warn('⚠️ Campaign fetch failed:', res.status);
          return FETCH_FAILED;
        }
        const data = await res.json();
        const result = data && data.active ? data : null;
        // Only successful answers are cached, so a failure is always retried
        // rather than remembered for 20s.
        campaignCache[placement] = { data: result, expiresAt: Date.now() + 20000 };
        return result;
      } catch (err) {
        console.warn('⚠️ Campaign fetch error:', err);
        return FETCH_FAILED;
      }
    }

    // ---------- Confetti ----------
    function fireConfetti() {
      if (!confettiHost) return;
      if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return;
      }
      confettiHost.innerHTML = '';
      for (let i = 0; i < 14; i++) {
        const piece = document.createElement('span');
        piece.className = 'discount-popup-confetti-piece';
        piece.textContent = CONFETTI_EMOJI[i % CONFETTI_EMOJI.length];
        const angle = (Math.PI * 2 * i) / 14 + (Math.random() * 0.5 - 0.25);
        const distance = 70 + Math.random() * 50;
        piece.style.setProperty('--dx', Math.cos(angle) * distance + 'px');
        piece.style.setProperty('--dy', Math.sin(angle) * distance + 'px');
        piece.style.setProperty('--rot', Math.random() * 360 + 'deg');
        piece.style.animationDelay = Math.random() * 0.15 + 's';
        confettiHost.appendChild(piece);
      }
      setTimeout(() => {
        confettiHost.innerHTML = '';
      }, 1200);
    }

    function shakeMessage() {
      messageEl.classList.remove('shake');
      void messageEl.offsetWidth;
      messageEl.classList.add('shake');
    }

    function getFocusableElements() {
      return Array.prototype.slice
        .call(overlay.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter(function (el) {
          return el.offsetParent !== null;
        });
    }

    function trapFocus(event) {
      const focusable = getFocusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    // ---------- Modal content ----------
    function applyCampaignToModal(campaign) {
      const collectFirst = campaign ? campaign.collectFirst !== false : true;
      const collectLast = campaign ? !!campaign.collectLast : true;
      const collectCountry = campaign ? !!campaign.collectCountry : false;
      const collectPhone = campaign ? !!campaign.collectPhone : false;

      if (titleEl) titleEl.textContent = campaign ? campaign.headline || 'Get your code' : 'Get 10% off';
      if (subtitleEl) {
        subtitleEl.textContent = campaign
          ? campaign.subtext || "Tell us a bit about yourself and we'll email you a discount code."
          : "Tell us a bit about yourself and we'll email you a one-time discount code.";
      }
      if (submitButton) {
        const label = campaign && campaign.buttonText ? campaign.buttonText : 'Get my code';
        // Build with a static emoji span + a text node for the (admin-authored)
        // label, so markup in buttonText can never execute on the storefront.
        submitButton.innerHTML = '<span aria-hidden="true">✨</span> ';
        submitButton.appendChild(document.createTextNode(label));
      }

      if (firstNameFieldEl) {
        firstNameFieldEl.hidden = !collectFirst;
        if (form.firstName) form.firstName.required = collectFirst;
      }
      if (lastNameFieldEl) {
        lastNameFieldEl.hidden = !collectLast;
        if (form.lastName) form.lastName.required = collectLast;
      }
      if (countryFieldEl) {
        countryFieldEl.hidden = !collectCountry;
      }
      if (phoneFieldEl) {
        phoneFieldEl.hidden = !collectPhone;
      }
    }

    // ---------- Open / Close ----------
    function openModal() {
      previouslyFocusedElement = document.activeElement;
      overlay.hidden = false;
      document.body.style.overflow = 'hidden';
      const focusable = getFocusableElements();
      (focusable[0] || closeButton).focus();
    }

    function closeModal() {
      overlay.hidden = true;
      document.body.style.overflow = '';
      if (previouslyFocusedElement && typeof previouslyFocusedElement.focus === 'function') {
        previouslyFocusedElement.focus();
      }
      previouslyFocusedElement = null;
    }

    async function openModalForTrigger(trigger) {
      activeTrigger = trigger;
      const placement = trigger.dataset.placement || null;
      const looked = PREVIEW_MODE ? (previewCampaignByPlacement[placement] || null) : await fetchActiveCampaign(placement);
      // For the modal, a failed lookup falls back to the snippet's static
      // defaults rather than blocking the customer from signing up.
      activeCampaign = looked === FETCH_FAILED ? null : looked;
      applyCampaignToModal(activeCampaign);
      form.reset();
      messageEl.textContent = '';
      messageEl.className = 'discount-popup-message';
      form.hidden = false;
      openModal();
    }

    // Modal-only wiring. Grouped behind modalReady so a page without the popup
    // snippet can still render the banner instead of throwing on a null
    // element and killing the rest of this script.
    if (modalReady) {
      // Attach to every trigger
      document.querySelectorAll('.discount-trigger-button').forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          openModalForTrigger(trigger);
        });
      });

      closeButton.addEventListener('click', closeModal);

      overlay.addEventListener('click', function (event) {
        if (event.target === overlay) {
          closeModal();
        }
      });

      document.addEventListener('keydown', function (event) {
        if (overlay.hidden) return;
        if (event.key === 'Escape') {
          closeModal();
        } else if (event.key === 'Tab') {
          trapFocus(event);
        }
      });
    }

    // ---------- Saved code ----------
    // The last code this browser was issued. Codes never expire, so the code is
    // all that is kept — there is no deadline to store and nothing to count down.
    function saveActiveCode(code) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ code: code }));
      } catch (err) {
        // localStorage unavailable
      }
    }

    function clearActiveCode() {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (err) {}
    }

    function loadActiveCode() {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch (err) {
        return null;
      }
    }

    // No discount-code text is ever painted onto the storefront — not over the
    // banner artwork, and not in a floating badge. The banner shows only its
    // image; the customer receives their code by email and on the signup page
    // right after submitting.

    // ---------- Submit ----------
    // Guarded for the same reason as the wiring above: no modal on this page
    // must not stop the banner from rendering.
    if (form) form.addEventListener('submit', async function (event) {
      event.preventDefault();
      console.log('📤 Form submitted');

      const firstName = form.firstName ? form.firstName.value.trim() : '';
      const lastName = form.lastName ? form.lastName.value.trim() : '';
      const email = form.email ? form.email.value.trim() : '';
      const country = form.country ? form.country.value.trim() : '';
      const phone = form.phone ? form.phone.value.trim() : '';
      const optedInMarketing = form.optedInMarketing ? form.optedInMarketing.checked : true;

      // Validate
      if (!firstName) {
        messageEl.textContent = '⚠️ Please enter your first name.';
        messageEl.className = 'discount-popup-message error';
        shakeMessage();
        return;
      }

      if (!EMAIL_REGEX.test(email)) {
        messageEl.textContent = '⚠️ Please enter a valid email address.';
        messageEl.className = 'discount-popup-message error';
        shakeMessage();
        return;
      }

      submitButton.disabled = true;
      submitButton.textContent = 'Sending...';
      messageEl.textContent = '';
      messageEl.className = 'discount-popup-message';

      const payload = {
        firstName: firstName,
        lastName: lastName || '',
        email: email,
        country: country || '',
        phone: phone || '',
        optedInMarketing: optedInMarketing,
        productId: activeTrigger ? activeTrigger.dataset.productId : undefined,
        productTitle: activeTrigger ? activeTrigger.dataset.productTitle : undefined,
        placement: activeTrigger ? activeTrigger.dataset.placement : undefined,
      };

      console.log('📤 Sending payload:', payload);

      // In preview mode (opened from the dashboard's Live Preview / Customer
      // preview), simulate rather than call the real API — unless the admin
      // is deliberately testing a campaign that's actually published
      // (isLiveTest), in which case a real end-to-end test is intentional.
      const simulateOnly = PREVIEW_MODE && !(activeCampaign && activeCampaign.isLiveTest);

      try {
        let data;
        if (simulateOnly) {
          await new Promise((resolve) => setTimeout(resolve, 400));
          // Deliberately not the campaign's real code: the public payload does
          // not carry it, so nobody can read a live code without signing up.
          const prefix = 'PREVIEW';
          data = { success: true, code: prefix + '-SIMULATED', applyUrl: null };
        } else {
          const response = await backendFetch('/api/subscribe', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify(payload),
          });

          console.log('📥 Response status:', response.status);

          try {
            data = await response.json();
          } catch (parseErr) {
            console.error('❌ Failed to parse response:', parseErr);
            throw new Error('Unexpected response from the server. Please try again.');
          }

          if (!response.ok || !data.success) {
            throw new Error((data && data.error) || 'Something went wrong.');
          }
        }

        console.log('✅ Success! Code:', data.code);

        messageEl.textContent = simulateOnly
          ? '✅ Preview simulation complete — no real email was sent.'
          : '✅ Success! Check your email for your discount code.';
        messageEl.className = 'discount-popup-message success';
        fireConfetti();
        form.hidden = true;

        if (!simulateOnly) saveActiveCode(data.code);

        if (PREVIEW_MODE && window.parent && window.parent !== window) {
          window.parent.postMessage({
            type: 'campaign-preview-submitted',
            code: data.code,
            email: email,
            isSimulated: simulateOnly,
          }, '*');
        }
      } catch (err) {
        console.error('❌ Submission error:', err);
        messageEl.textContent = '❌ ' + (err.message || 'Something went wrong. Please try again.');
        messageEl.className = 'discount-popup-message error';
        shakeMessage();
      } finally {
        submitButton.disabled = false;
        submitButton.innerHTML = submitButtonDefaultHTML;
      }
    });

    // ---------- Home page banner hydration ----------
    // BANNER_HEIGHTS must stay in sync with the size presets offered in the
    // dashboard's campaign builder (dashboard/js/dashboard.js campSetBannerHeight).
    const BANNER_HEIGHTS = { small: '160px', medium: '280px', large: '420px' };

    // Sizes a banner element to its image's aspect ratio so its height follows
    // its width — the same rendering the dashboard preview uses, so the live
    // banner matches what the admin framed. Sets a sensible default immediately
    // to avoid layout shift, then corrects to the image's true ratio on load.
    if (bannerEl) {
      const bannerInnerEl = bannerEl.querySelector('.discount-banner__inner') || bannerEl;
      const clickableEl = bannerEl.querySelector('[data-discount-banner-clickable]') || bannerInnerEl;

      // Which campaign the single banner currently on screen is — set by
      // renderBannerFromCampaign() below, cleared by hideBanner() — so a click
      // opens THAT campaign's own landing page (?campaign=<slug>) instead of
      // the generic "whichever campaign is active" page.
      let currentBannerCampaignParam = '';

      function openSignupPage() {
        // A clean, symbol-free link (…/<slug>, no ?placement=&campaign=&lang=)
        // for the normal case — the server resolves placement/language itself
        // from the campaign. Preview mode keeps the query-string form: it
        // needs ?preview=1 (and there's no "real" link to keep clean anyway).
        // Opens in a new tab — same for every campaign, solo or grouped, and
        // shoppers keep the store open behind it instead of losing their place.
        if (currentBannerCampaignParam && !PREVIEW_MODE) {
          window.open(BACKEND_URL + '/' + encodeURIComponent(currentBannerCampaignParam), '_blank', 'noopener');
          return;
        }
        var campaignParam = currentBannerCampaignParam ? '&campaign=' + encodeURIComponent(currentBannerCampaignParam) : '';
        var url = BACKEND_URL + '/signup?placement=home' + campaignParam + storeLangParam() + (PREVIEW_MODE ? '&preview=1' : '');
        if (PREVIEW_MODE) { window.location.href = url; return; }
        window.open(url, '_blank', 'noopener');
      }
      clickableEl.addEventListener('click', openSignupPage);
      clickableEl.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openSignupPage();
        }
      });

      // ---------- Banner image pan/zoom editor ----------
      // Banners are always image-only now (no text/button overlay for new
      // campaigns) — instead of repositioning text, the admin can pan/zoom
      // the image itself. Applies to every banner with an image, not just
      // an opt-in mode; edit-mode adds the interactive drag/zoom controls
      // on top of the same rendering.
      let bannerImageEditorAttached = false;

      function applyBannerImageTransform(campaign) {
        const x = campaign.bannerImageX != null ? campaign.bannerImageX : 50;
        const y = campaign.bannerImageY != null ? campaign.bannerImageY : 50;
        const zoom = campaign.bannerImageZoom != null ? campaign.bannerImageZoom : 100;
        bannerInnerEl.style.backgroundPosition = x + '% ' + y + '%';
        bannerInnerEl.style.backgroundSize = zoom === 100 ? 'cover' : zoom + '%';
      }

      function attachBannerImageEditor(campaign) {
        if (bannerImageEditorAttached) return;
        bannerImageEditorAttached = true;
        bannerInnerEl.style.overflow = 'hidden';

        let x = campaign.bannerImageX != null ? campaign.bannerImageX : 50;
        let y = campaign.bannerImageY != null ? campaign.bannerImageY : 50;
        let zoom = campaign.bannerImageZoom != null ? campaign.bannerImageZoom : 100;

        function commit() {
          bannerInnerEl.style.backgroundPosition = x + '% ' + y + '%';
          bannerInnerEl.style.backgroundSize = zoom === 100 ? 'cover' : zoom + '%';
          if (window.parent && window.parent !== window) {
            window.parent.postMessage({ type: 'campaign-preview-image-changed', x: x, y: y, zoom: zoom }, '*');
          }
        }

        function clamp(n, min, max) { return Math.max(min, Math.min(max, n)); }

        bannerInnerEl.classList.add('discount-banner__inner--editing');
        bannerInnerEl.addEventListener('pointerdown', function (event) {
          if (event.target.closest('.ce-zoom-controls')) return;
          const startX = event.clientX;
          const startY = event.clientY;
          const startPanX = x;
          const startPanY = y;
          const rect = bannerInnerEl.getBoundingClientRect();
          bannerInnerEl.setPointerCapture(event.pointerId);
          function onMove(e) {
            // Dragging right/down reveals more of the image's left/top side
            // (standard photo-pan feel) — so the offset subtracts the delta.
            const dxPct = ((e.clientX - startX) / rect.width) * 100;
            const dyPct = ((e.clientY - startY) / rect.height) * 100;
            x = clamp(startPanX - dxPct, 0, 100);
            y = clamp(startPanY - dyPct, 0, 100);
            bannerInnerEl.style.backgroundPosition = x + '% ' + y + '%';
          }
          function onUp(e) {
            bannerInnerEl.releasePointerCapture(e.pointerId);
            bannerInnerEl.removeEventListener('pointermove', onMove);
            bannerInnerEl.removeEventListener('pointerup', onUp);
            commit();
          }
          bannerInnerEl.addEventListener('pointermove', onMove);
          bannerInnerEl.addEventListener('pointerup', onUp);
        });

        const zoomBar = document.createElement('div');
        zoomBar.className = 'ce-zoom-controls';
        const zoomOut = document.createElement('button');
        zoomOut.type = 'button';
        zoomOut.className = 'ce-zoom-btn';
        zoomOut.textContent = '−';
        const zoomLabel = document.createElement('span');
        zoomLabel.className = 'ce-zoom-label';
        zoomLabel.textContent = Math.round(zoom) + '%';
        const zoomIn = document.createElement('button');
        zoomIn.type = 'button';
        zoomIn.className = 'ce-zoom-btn';
        zoomIn.textContent = '+';
        zoomBar.appendChild(zoomOut);
        zoomBar.appendChild(zoomLabel);
        zoomBar.appendChild(zoomIn);
        bannerInnerEl.appendChild(zoomBar);

        function changeZoom(delta) {
          zoom = clamp(zoom + delta, 100, 400);
          zoomLabel.textContent = Math.round(zoom) + '%';
          bannerInnerEl.style.backgroundSize = zoom === 100 ? 'cover' : zoom + '%';
          commit();
        }
        zoomOut.addEventListener('click', function (e) { e.stopPropagation(); changeZoom(-10); });
        zoomIn.addEventListener('click', function (e) { e.stopPropagation(); changeZoom(10); });
      }

      // Fully collapse the banner slot — hidden AND all inline styling cleared,
      // so nothing (no colour block, no reserved height) is left behind when no
      // campaign is live, before a scheduled start, or after an end.
      function hideBanner() {
        bannerEl.hidden = true;
        bannerInnerEl.style.background = '';
        bannerInnerEl.style.backgroundColor = '';
        bannerInnerEl.style.minHeight = '';
        bannerInnerEl.style.aspectRatio = '';
        bannerInnerEl.removeAttribute('data-shown-url');
        currentBannerCampaignParam = '';
      }

      function renderBannerFromCampaign(campaign) {
        if (!campaign || !campaign.active) { hideBanner(); return; }
        currentBannerCampaignParam = campaign.urlSlug || (campaign.id != null ? String(campaign.id) : '');
        // Drop any leftover multi-banner wrappers (strip/mosaic/grid/stack) so a
        // returning single banner never overlaps stale cards.
        Array.prototype.forEach.call(
          bannerInnerEl.querySelectorAll('.discount-banner__multi'),
          function (el) { el.remove(); }
        );
        stopStripAuto();

        // Banners are image-only: always clear the legacy Liquid text/overlay
        // nodes so nothing can leak into view.
        [
          bannerEl.querySelector('[data-discount-banner-eyebrow]'),
          bannerEl.querySelector('[data-discount-banner-heading]'),
          bannerEl.querySelector('[data-discount-banner-subtext]'),
          bannerEl.querySelector('[data-discount-banner-cta]'),
        ].forEach(function (el) { if (el) { el.textContent = ''; el.style.display = 'none'; } });

        var resolvedBannerUrl = resolveBannerUrl(campaign.bannerUrl);
        // No image → show NOTHING (never a coloured placeholder block).
        if (!resolvedBannerUrl) { hideBanner(); return; }

        // Same image already on screen → just re-apply framing (no flicker on
        // the 10s poll / live re-renders).
        if (bannerInnerEl.getAttribute('data-shown-url') === resolvedBannerUrl && !bannerEl.hidden) {
          applyBannerImageTransform(campaign);
          if (EDIT_MODE) attachBannerImageEditor(campaign);
          return;
        }

        // Preload the image and only reveal the banner once it has actually
        // loaded — so the customer sees the banner appear at its scheduled time,
        // never an empty green box first. On error, stay collapsed.
        var probe = new Image();
        probe.onload = function () {
          bannerInnerEl.style.boxSizing = 'border-box';
          bannerInnerEl.style.padding = '0';
          bannerInnerEl.style.minHeight = '';
          bannerInnerEl.style.background = 'url("' + resolvedBannerUrl + '") center/cover no-repeat';
          bannerInnerEl.style.backgroundColor = 'transparent';
          if (probe.naturalWidth && probe.naturalHeight) {
            bannerInnerEl.style.aspectRatio = probe.naturalWidth + ' / ' + probe.naturalHeight;
          }
          applyBannerImageTransform(campaign);
          if (EDIT_MODE) attachBannerImageEditor(campaign);
          bannerInnerEl.setAttribute('data-shown-url', resolvedBannerUrl);
          bannerEl.hidden = false;
        };
        probe.onerror = function () { hideBanner(); };
        probe.src = resolvedBannerUrl;
      }

      // ---------- Multi-banner strip ----------
      // When several campaigns are published to the home placement at once (the
      // dashboard's "Publish together" action), they appear as a horizontal,
      // swipeable strip of banner cards in this same slot — like the promo card
      // rows on large storefronts. A single banner still renders full-width.
      var stripStyleInjected = false;
      var stripRAF = null; // setInterval handle for the many-banner auto-rotate
      // Stops (and clears) the auto-rotate timer. Called before any re-render so
      // switching layout/count/scope never leaves an old timer scrolling a strip
      // that no longer exists.
      function stopStripAuto() { if (stripRAF) { clearInterval(stripRAF); stripRAF = null; } }
      function injectStripStyles() {
        if (stripStyleInjected) return; stripStyleInjected = true;
        var st = document.createElement('style');
        st.textContent = '.discount-banner__card{transition:transform .15s ease, box-shadow .15s ease;}'
          + '.discount-banner__card:hover{transform:translateY(-2px);box-shadow:0 8px 20px rgba(0,0,0,.16);}'
          // Chrome/Safari/Edge ignore scrollbar-width; this is their equivalent.
          + '.discount-banner__strip::-webkit-scrollbar{display:none;}';
        document.head.appendChild(st);
      }

      // `layout` (carousel | mosaic | grid | stack) is the store-wide
      // arrangement the admin picked in the "Publish multiple" composer. A
      // single banner ignores it — every layout collapses to one full-width
      // banner — so it only matters once two or more are live.
      // Remember the last render so we can reflow it responsively when the
      // viewport crosses the mobile/desktop breakpoint (the mosaic/grid column
      // counts are chosen at render time), without another network fetch.
      var lastBanners = null, lastLayout = 'carousel';
      function renderBanners(banners, layout) {
        lastBanners = banners; lastLayout = layout || 'carousel';
        if (!banners || !banners.length) { bannerEl.hidden = true; return; }
        if (banners.length === 1) {
          // renderBannerFromCampaign clears any leftover multi-banner wrappers
          // (strip / mosaic / grid / stack) before painting the single banner.
          renderBannerFromCampaign(banners[0]);
          return;
        }
        switch (lastLayout) {
          case 'mosaic': renderBannerMosaic(banners); break;
          case 'grid': renderBannerGrid(banners); break;
          case 'stack': renderBannerStack(banners); break;
          default: renderBannerStrip(banners);
        }
      }

      // Reflow the multi-banner layout on resize (debounced) — every settle,
      // not just when a single narrow/wide breakpoint flips. The grid layout's
      // column count is now width-driven (see renderBannerGrid) rather than a
      // single binary switch, so there is more than one width where the
      // column count actually needs to change; re-rendering unconditionally
      // on every debounced resize is cheap (a handful of DOM nodes) and covers
      // all of them, including mosaic/grid's own narrow/wide split.
      var resizeT = null;
      window.addEventListener('resize', function () {
        if (!lastBanners || lastBanners.length < 2) return;
        clearTimeout(resizeT);
        resizeT = setTimeout(function () {
          renderBanners(lastBanners, lastLayout);
        }, 180);
      });

      // Resets the banner slot to a plain block box the layouts can fill, and
      // clears any prior strip/auto-scroll. Shared by the non-carousel layouts.
      function prepBannerContainer() {
        var inner = bannerInnerEl;
        inner.style.display = 'block';
        inner.style.boxSizing = 'border-box';
        inner.style.padding = '6px 14px 14px';
        inner.style.background = '';
        inner.style.aspectRatio = '';
        inner.style.minHeight = '';
        inner.style.overflow = 'hidden';
        inner.style.width = '100%';
        inner.innerHTML = '';
        inner.removeAttribute('data-shown-url'); // so a later single banner re-renders cleanly
        stopStripAuto();
        return inner;
      }

      // One banner card = just the merchant's banner image, shown at its own
      // natural aspect ratio and full width of the card. No crop, no blurred
      // fill, no overlaid discount label — nothing added on top of the image.
      // The card's height comes from the image, so nothing is ever cut off.
      function buildBannerCard(c, styleCss) {
        var url = resolveBannerUrl(c.bannerUrl);
        var card = document.createElement('button');
        card.type = 'button';
        card.className = 'discount-banner__card';
        card.setAttribute('data-campaign-id', c.id);
        card.setAttribute('aria-label', c.headline || 'Discount banner');
        // Square corners, matching the single-banner path (.discount-banner__inner
        // has none either) — every campaign banner reads as one consistent shape
        // whether it's shown alone or grouped with others.
        card.style.cssText = 'display:block;border:none;padding:0;cursor:pointer;overflow:hidden;'
          + 'background:#f2f3f0;border-radius:0;box-shadow:0 2px 10px rgba(0,0,0,.10);' + (styleCss || '');
        if (url) {
          var img = document.createElement('img');
          img.src = url;
          img.alt = c.headline || '';
          img.loading = 'lazy';
          img.style.cssText = 'display:block;width:100%;height:auto;';
          card.appendChild(img);
        }
        card.addEventListener('click', function (e) {
          e.stopPropagation();
          var campaignParam = encodeURIComponent(c.urlSlug || c.id);
          // Same clean-link preference, and same new-tab behavior, as the
          // single-banner path above — every campaign in the set opens the
          // same way.
          if (!PREVIEW_MODE) { window.open(BACKEND_URL + '/' + campaignParam, '_blank', 'noopener'); return; }
          window.location.href = BACKEND_URL + '/signup?placement=home&campaign=' + campaignParam + storeLangParam() + '&preview=1';
        });
        return card;
      }

      // Mosaic — the first banner featured (wider column) beside the rest stacked
      // in a narrower column. Each image keeps its natural shape. One column on
      // narrow screens.
      function renderBannerMosaic(banners) {
        injectStripStyles();
        var inner = prepBannerContainer();
        var narrow = window.matchMedia && window.matchMedia('(max-width: 600px)').matches;
        var wrap = document.createElement('div');
        wrap.style.cssText = 'display:grid;gap:12px;width:100%;box-sizing:border-box;align-items:start;'
          + 'grid-template-columns:' + (narrow ? '1fr' : '1.7fr 1fr') + ';';
        wrap.className = 'discount-banner__multi';
        wrap.appendChild(buildBannerCard(banners[0], 'width:100%;'));
        var col = document.createElement('div');
        col.style.cssText = 'display:flex;flex-direction:column;gap:12px;min-width:0;';
        banners.slice(1).forEach(function (c) { col.appendChild(buildBannerCard(c, 'width:100%;')); });
        wrap.appendChild(col);
        inner.appendChild(wrap);
        bannerEl.hidden = false;
      }

      // Even grid — equal-width columns (2 on mobile, 3 on desktop); each image
      // keeps its natural height, aligned to the top of its cell.
      //
      // Flexbox, not CSS grid: a real grid keeps every row's cells fixed to
      // their column, so a count that isn't a multiple of `cols` (the common
      // case right after stopping one campaign out of the group) strands the
      // last row's card(s) flush against the LEFT edge with an empty, very
      // visible gap where the grid still reserves the missing cell — reading
      // exactly like a banner failed to load rather than "there are just
      // fewer today". Flex-wrap has no such reserved cells: an incomplete row
      // simply has fewer items, and `justify-content:center` centers them
      // instead of leaving them stranded to one side.
      function renderBannerGrid(banners) {
        injectStripStyles();
        var inner = prepBannerContainer();
        var gap = 12;
        var minCard = 220;
        // Column count is derived from the space actually available, not a
        // single narrow/wide breakpoint — that used to request 3 columns for
        // any width over 600px, but 3 cards at the 220px min-width below
        // don't actually fit until ~870px wide. Everything from 600px to
        // ~870px silently lost a column to wrapping, in a way that changed
        // unpredictably with the exact width. Sizing cols to what min-width
        // really allows keeps the two numbers consistent at every width.
        var availW = inner.clientWidth || (window.innerWidth - 28);
        var maxFit = Math.max(1, Math.floor((availW + gap) / (minCard + gap)));
        var cols = Math.min(3, maxFit);
        var basis = 'calc((100% - ' + (gap * (cols - 1)) + 'px) / ' + cols + ')';
        var grid = document.createElement('div');
        grid.className = 'discount-banner__multi';
        grid.style.cssText = 'display:flex;flex-wrap:wrap;gap:' + gap + 'px;width:100%;box-sizing:border-box;align-items:flex-start;justify-content:center;';
        banners.forEach(function (c) { grid.appendChild(buildBannerCard(c, 'flex:0 1 ' + basis + ';max-width:' + basis + ';min-width:min(' + minCard + 'px,100%);')); });
        inner.appendChild(grid);
        bannerEl.hidden = false;
      }

      // Vertical stack — full-width banners one above another, each at its own
      // natural aspect ratio.
      function renderBannerStack(banners) {
        injectStripStyles();
        var inner = prepBannerContainer();
        var col = document.createElement('div');
        col.className = 'discount-banner__multi';
        col.style.cssText = 'display:flex;flex-direction:column;gap:12px;width:100%;box-sizing:border-box;';
        banners.forEach(function (c) { col.appendChild(buildBannerCard(c, 'width:100%;')); });
        inner.appendChild(col);
        bannerEl.hidden = false;
      }

      function renderBannerStrip(banners) {
        injectStripStyles();
        var inner = bannerInnerEl;
        // Plain block box so the row is constrained to the banner's width and
        // never overflows the page horizontally.
        inner.style.display = 'block';
        inner.style.boxSizing = 'border-box';
        inner.style.padding = '0';
        inner.style.background = '';
        inner.style.aspectRatio = '';
        inner.style.minHeight = '';
        inner.style.overflow = 'hidden';
        inner.style.width = '100%';
        inner.style.maxWidth = '100%';
        inner.innerHTML = '';
        inner.removeAttribute('data-shown-url');

        stopStripAuto();

        var strip = document.createElement('div');
        strip.className = 'discount-banner__strip discount-banner__multi';

        // Up to 3 banners share the row equally — all fully visible at once,
        // each shrinking a bit as more join. With more than that, shrinking
        // further makes each one unreadable, so the row becomes a proper
        // carousel instead: every card keeps its own fixed, readable width
        // regardless of how many there are, the row scrolls (swipe / trackpad
        // / scrollbar), and it auto-rotates through the banners, wrapping back
        // to the first after the last — a real circular carousel.
        var MANY = banners.length > 3;

        if (!MANY) {
          strip.style.cssText = 'display:flex;gap:12px;width:100%;max-width:100%;box-sizing:border-box;padding:6px 14px 14px;align-items:flex-start;';
          banners.forEach(function (c) {
            strip.appendChild(buildBannerCard(c, 'flex:1 1 0;min-width:0;'));
          });
          inner.appendChild(strip);
          bannerEl.hidden = false;
          return;
        }

        var narrow = window.matchMedia && window.matchMedia('(max-width: 600px)').matches;
        // Card width: ~1 per view on phones, ~3 across on desktop, so the next
        // card peeks to signal "scroll for more".
        var basis = narrow ? '84%' : '31%';
        // The scrollbar itself is hidden (Firefox: scrollbar-width, old Edge/IE:
        // -ms-overflow-style, everyone else: the ::-webkit-scrollbar rule in
        // injectStripStyles) — scrolling still works by swipe/trackpad/wheel and
        // the auto-rotate below, so the bar was pure visual clutter, not the
        // only way to move through the banners.
        strip.style.cssText = 'position:relative;display:flex;gap:12px;width:100%;max-width:100%;box-sizing:border-box;'
          + 'padding:6px 14px 14px;align-items:flex-start;overflow-x:auto;overflow-y:hidden;'
          + 'scroll-snap-type:x mandatory;-webkit-overflow-scrolling:touch;scrollbar-width:none;-ms-overflow-style:none;';
        banners.forEach(function (c) {
          strip.appendChild(buildBannerCard(c, 'flex:0 0 ' + basis + ';min-width:0;scroll-snap-align:start;'));
        });
        inner.appendChild(strip);
        bannerEl.hidden = false;

        // Auto-rotate: advance one card at a time, wrapping to the start. Pauses
        // while the visitor is hovering/swiping/scrolling so it never fights
        // them, and is skipped entirely for reduced-motion users (they can still
        // scroll by hand).
        var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduce) return;
        var paused = false, resumeT = null, idx = 0;
        function hold() { paused = true; clearTimeout(resumeT); }
        function release() { clearTimeout(resumeT); resumeT = setTimeout(function () { paused = false; }, 4000); }
        strip.addEventListener('mouseenter', hold);
        strip.addEventListener('mouseleave', release);
        strip.addEventListener('touchstart', hold, { passive: true });
        strip.addEventListener('touchend', release, { passive: true });
        strip.addEventListener('wheel', function () { hold(); release(); }, { passive: true });
        stripRAF = setInterval(function () {
          if (paused) return;
          var cards = strip.children;
          if (cards.length < 2) return;
          idx = (idx + 1) % cards.length;
          // scroll-snap corrects any small offset, so approximate is fine.
          strip.scrollTo({ left: cards[idx].offsetLeft, behavior: 'smooth' });
        }, 3800);
      }

      // Fetches ALL published home banners (the carousel set). Returns an array,
      // or FETCH_FAILED so callers keep the current banner instead of blanking.
      function fetchActiveList(force) {
        var path = '/api/campaigns/active-list?placement=home' + (force ? '&t=' + Date.now() : '');
        return backendFetch(path).then(function (res) {
          if (!res.ok) return FETCH_FAILED;
          return res.json().then(function (data) {
            return { banners: (data && data.banners) || [], layout: (data && data.layout) || 'carousel' };
          });
        }).catch(function () { return FETCH_FAILED; });
      }

      if (PREVIEW_MODE) {
        // Driven entirely by postMessage from the dashboard's builder — no
        // network fetch, so unsaved draft edits (including a brand-new,
        // never-published campaign) render immediately.
        window.addEventListener('message', function (event) {
          if (!event.data || event.data.type !== 'campaign-preview-render') return;
          if (event.data.placement && event.data.placement !== 'home') return;
          renderBannerFromCampaign(event.data.campaign);
        });
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({ type: 'campaign-preview-ready', placement: 'home' }, '*');
        }
      } else {
        // Retry a failed lookup with backoff instead of leaving the banner
        // blank for the rest of the pageview. The banner's own visibility is
        // never touched on failure — only a definite answer changes it.
        const BANNER_RETRY_DELAYS = [800, 1600, 3200, 6400, 15000];
        function loadBannerWithRetry(attempt) {
          attempt = attempt || 0;
          // force from the 2nd attempt on, so a retry can't be served the
          // cached failure-adjacent state.
          fetchActiveList(attempt > 0).then(function (result) {
            if (result === FETCH_FAILED) {
              const delay = BANNER_RETRY_DELAYS[Math.min(attempt, BANNER_RETRY_DELAYS.length - 1)];
              console.warn('⚠️ Banner: campaign lookup failed — retrying in ' + delay + 'ms (attempt ' + (attempt + 1) + ')');
              setTimeout(function () { loadBannerWithRetry(attempt + 1); }, delay);
              return;
            }
            renderBanners(result.banners, result.layout);
          });
        }
        loadBannerWithRetry();

        // ---------- Live updates ----------
        // Any campaign change in the dashboard (create, edit, publish, stop,
        // archive, restore, delete, new banner image) re-renders this banner
        // immediately — no page refresh. The backend pushes a
        // 'campaign-change' Server-Sent Event from invalidateCache(), which
        // every mutation already calls.
        function refreshBannerNow() {
          // force=true skips caches so the very next paint reflects what the
          // admin just saved. A failed refresh leaves the banner(s) as they are.
          fetchActiveList(true).then(function (result) {
            if (result === FETCH_FAILED) return;
            renderBanners(result.banners, result.layout);
          });
        }

        var pollTimer = null;
        function startPollingFallback() {
          if (pollTimer) return;
          // 10s: this is the path the real storefront actually uses whenever the
          // tunnel buffers SSE, so it has to feel prompt rather than be a token
          // safety net. The answer is served from a 30s server cache and the
          // payload is tiny, so the cost is negligible.
          console.log('🔁 Live updates: polling every 10s');
          refreshBannerNow();
          pollTimer = setInterval(refreshBannerNow, 10000);
        }
        function stopPollingFallback() {
          if (!pollTimer) return;
          clearInterval(pollTimer);
          pollTimer = null;
        }

        if (typeof window.EventSource === 'function') {
          var es = null;
          var reconnects = 0;
          var lastMessageAt = 0;
          var openedAt = 0;
          var watchdog = null;

          // A buffering proxy (the Cloudflare quick tunnel) answers 200 with the
          // right content type and then delivers nothing. The connection counts
          // as OPEN, so 'error' never fires and a purely error-based fallback
          // would leave the storefront with no updates at all. So health is
          // judged by SILENCE: no handshake soon after opening, or no frame for
          // longer than two heartbeats, means give up on SSE and poll instead.
          var HANDSHAKE_GRACE_MS = 8000;   // must hear 'connected' within this
          var SILENCE_LIMIT_MS = 40000;    // heartbeat is 15s, so 40s = missed 2

          function noteMessage() {
            lastMessageAt = Date.now();
            stopPollingFallback(); // stream is proven alive
          }

          function startWatchdog() {
            openedAt = Date.now();
            clearInterval(watchdog);
            watchdog = setInterval(function () {
              // Before the first frame there is no timestamp to measure from,
              // so age the connection from when the watchdog started instead —
              // otherwise the elapsed time reads as the whole Unix epoch.
              var since = lastMessageAt || openedAt;
              var quietFor = Date.now() - since;
              var limit = lastMessageAt ? SILENCE_LIMIT_MS : HANDSHAKE_GRACE_MS;
              if (quietFor > limit) {
                console.warn('📡 Live updates: no data for ' + Math.round(quietFor / 1000) + 's — using polling instead');
                if (es) { es.close(); es = null; }
                clearInterval(watchdog);
                watchdog = null;
                startPollingFallback();
              }
            }, 4000);
          }

          function connectEvents() {
            try {
              es = new EventSource(BACKEND_URL + '/api/campaigns/events');
            } catch (err) {
              startPollingFallback();
              return;
            }
            lastMessageAt = 0;
            startWatchdog();

            es.addEventListener('connected', function () {
              reconnects = 0;
              noteMessage();
              console.log('📡 Live updates connected');
            });
            es.addEventListener('ping', noteMessage);
            es.addEventListener('campaign-change', function () {
              noteMessage();
              console.log('📡 Campaign changed — re-rendering banner');
              refreshBannerNow();
            });
            es.onerror = function () {
              // EventSource retries on its own; only after it keeps failing do
              // we conclude SSE cannot get through and switch to polling.
              reconnects += 1;
              if (reconnects >= 3) {
                if (es) { es.close(); es = null; }
                clearInterval(watchdog);
                watchdog = null;
                startPollingFallback();
              }
            };
          }
          connectEvents();
        } else {
          startPollingFallback();
        }

        // Returning to the tab is the moment stale content is most visible, and
        // it is exactly the real workflow: the admin changes a campaign in the
        // dashboard tab, then switches to the store tab to look. Refresh on
        // every way a tab can come back to the foreground, so that switch shows
        // the change immediately instead of waiting out the poll interval.
        // 'focus' and 'visibilitychange' fire in different situations depending
        // on the browser, and 'pageshow' covers a back/forward cache restore
        // where no other event fires at all.
        document.addEventListener('visibilitychange', function () {
          if (!document.hidden) refreshBannerNow();
        });
        window.addEventListener('focus', refreshBannerNow);
        window.addEventListener('pageshow', function (event) {
          // persisted === true means the page came from the back/forward cache,
          // so its banner state can be arbitrarily old.
          if (event.persisted) refreshBannerNow();
        });
      }

      window.addEventListener('message', function (event) {
        if (!event.data || event.data.type !== 'discount-code-issued') return;
        saveActiveCode(event.data.code);
      });
    }
  });
})();